/**
 * ConstellationTimeline — History セクションのシグネチャー演出。
 * タイムラインの各エントリ（.timeline-date）を星のノードとして測定し、
 * それらを結ぶ星図（SVG polyline）をスクロールに合わせて上から描画していく。
 * 人生の出来事が一つの星座として結ばれる、というこのサイトの物語装置。
 *
 * - JSが失敗しても .timeline::before の薄いフォールバック線が残る
 * - prefers-reduced-motion 時は最初から全て描画済みにする
 */
export default class ConstellationTimeline {
    constructor() {
        this.timeline = document.querySelector('.timeline');
        if (!this.timeline) return;

        this.items = Array.from(this.timeline.querySelectorAll('.timeline-item'));
        if (!this.items.length) return;

        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.revealedCount = this.reducedMotion ? this.items.length : 0;
        this.svg = null;
        this.path = null;
        this.nodes = [];
        this.halos = [];
        this.points = [];
        this.totalLength = 0;
        this.resizeTimer = null;

        // フォント読み込みでレイアウトが動くので、確定後に測り直す
        this.build();
        if (document.fonts && document.fonts.ready) {
            document.fonts.ready.then(() => this.rebuild());
        }

        window.addEventListener('resize', () => {
            clearTimeout(this.resizeTimer);
            this.resizeTimer = setTimeout(() => this.rebuild(), 200);
        });

        if (!this.reducedMotion) {
            this.observeItems();
        }
    }

    // 各 .timeline-date の中心を .timeline 基準の座標で測る
    measurePoints() {
        const timelineRect = this.timeline.getBoundingClientRect();
        return this.items.map(item => {
            const date = item.querySelector('.timeline-date');
            const rect = (date || item).getBoundingClientRect();
            return {
                x: rect.left + rect.width / 2 - timelineRect.left,
                y: rect.top + rect.height / 2 - timelineRect.top
            };
        });
    }

    build() {
        this.points = this.measurePoints();
        if (this.points.some(p => !isFinite(p.x) || !isFinite(p.y))) return;

        const ns = 'http://www.w3.org/2000/svg';
        this.svg = document.createElementNS(ns, 'svg');
        this.svg.classList.add('constellation-svg');
        this.svg.setAttribute('aria-hidden', 'true');

        // 星座を結ぶ線
        this.path = document.createElementNS(ns, 'polyline');
        this.path.classList.add('constellation-path');
        this.path.setAttribute('points', this.points.map(p => `${p.x},${p.y}`).join(' '));
        this.svg.appendChild(this.path);

        // まわりに散らす小さな瞬き星（装飾・決定論的な配置）
        const twinkles = [
            [0.16, 0.08], [0.82, 0.14], [0.30, 0.30], [0.72, 0.42],
            [0.12, 0.55], [0.88, 0.66], [0.24, 0.82], [0.66, 0.92]
        ];
        const timelineRect = this.timeline.getBoundingClientRect();
        twinkles.forEach(([fx, fy], i) => {
            const star = document.createElementNS(ns, 'circle');
            star.classList.add('constellation-twinkle');
            star.setAttribute('cx', fx * timelineRect.width);
            star.setAttribute('cy', fy * timelineRect.height);
            star.setAttribute('r', 1);
            star.style.animationDelay = `${i * 0.7}s`;
            this.svg.appendChild(star);
        });

        // 各エントリの星ノード。先頭（最新=2025）は ember の「現在地」
        this.nodes = [];
        this.halos = [];
        this.points.forEach((p, i) => {
            const isCurrent = i === 0;

            const halo = document.createElementNS(ns, 'circle');
            halo.classList.add('constellation-node-halo');
            halo.setAttribute('cx', p.x);
            halo.setAttribute('cy', p.y);
            halo.setAttribute('r', isCurrent ? 10 : 7);
            this.svg.appendChild(halo);
            this.halos.push(halo);

            const node = document.createElementNS(ns, 'circle');
            node.classList.add('constellation-node');
            if (isCurrent) node.classList.add('is-current');
            node.setAttribute('cx', p.x);
            node.setAttribute('cy', p.y);
            node.setAttribute('r', isCurrent ? 4 : 3);
            this.svg.appendChild(node);
            this.nodes.push(node);
        });

        this.timeline.appendChild(this.svg);

        // 線を「未描画」状態にセットしてから、既に明かされた分だけ描く
        this.totalLength = this.path.getTotalLength();
        this.path.style.strokeDasharray = `${this.totalLength}`;
        this.applyProgress();
    }

    // reveal済みノード数に応じて dashoffset とノードの点灯を更新
    applyProgress() {
        if (!this.path) return;

        const drawn = this.lengthUpTo(this.revealedCount);
        this.path.style.strokeDashoffset = `${this.totalLength - drawn}`;

        this.nodes.forEach((node, i) => {
            node.classList.toggle('is-lit', i < this.revealedCount);
        });
        this.halos.forEach((halo, i) => {
            halo.classList.toggle('is-lit', i < this.revealedCount);
        });
    }

    // 先頭から n ノード目までの線の長さ
    lengthUpTo(n) {
        if (n <= 1) return 0;
        let length = 0;
        for (let i = 1; i < Math.min(n, this.points.length); i++) {
            const a = this.points[i - 1];
            const b = this.points[i];
            length += Math.hypot(b.x - a.x, b.y - a.y);
        }
        return length;
    }

    observeItems() {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (!entry.isIntersecting) return;
                const index = this.items.indexOf(entry.target);
                if (index >= this.revealedCount - 1) {
                    this.revealedCount = index + 1;
                    this.applyProgress();
                }
                observer.unobserve(entry.target);
            });
        }, { threshold: 0.5 });

        this.items.forEach(item => observer.observe(item));
    }

    rebuild() {
        if (this.svg) {
            this.svg.remove();
            this.svg = null;
        }
        this.build();
    }
}
